/**
 * Schema.org JSON-LD factories for SNAP Windows.
 *
 * Strict rules per business decision:
 *   - Real, verified data only — no invented address/reviews/founded date.
 *   - Production-domain only injection (the SchemaMarkup component below
 *     gates on hostname). Preview/dev domains never emit JSON-LD so we
 *     don't compete with the canonical production page in Google's index.
 *   - All `url` / `image` / `logo` values resolve to PRODUCTION_BASE so
 *     even if a developer accidentally renders these on a non-prod build
 *     the URLs still point at the canonical site.
 */

// -- Constants -----------------------------------------------------------
export const PRODUCTION_BASE = 'https://snap-windows.com';

export const PRODUCTION_HOSTS = new Set([
  'snap-windows.com',
  'www.snap-windows.com',
]);

// Real logo asset shipped at /app/frontend/public/logos/logo-light-bg.png
export const PRODUCTION_LOGO_URL = `${PRODUCTION_BASE}/logos/logo-light-bg.png`;

// Verified business identity (provided by user)
export const BUSINESS_INFO = {
  legalName: 'SNAP Windows LLC',
  brandName: 'SNAP Windows',
  phone: '+1-855-784-7627',
  phoneDisplay: '(855) 784-7627',
  // Florida CGC contractor license — surfaced per user request
  licenseNumber: 'CGC1524851',
  // No street address provided yet — service-area business model.
  // We INTENTIONALLY omit `address` to avoid publishing unverified data.
  // We also set `areaServed` (counties) instead of a postal address.
  serviceAreas: ['Miami-Dade County', 'Broward County', 'Palm Beach County'],
  serviceState: 'FL',
  hours: [
    { days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '08:00', closes: '18:00' },
    { days: ['Saturday'], opens: '09:00', closes: '14:00' },
    // Sunday closed — omitted (Schema.org best practice)
  ],
};

// Map page-relative paths to absolute production URLs. Used by every
// factory so the schema's `url` always references the canonical domain.
export function toProductionUrl(pathOrUrl = '/') {
  if (!pathOrUrl) return PRODUCTION_BASE;
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) return pathOrUrl;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${PRODUCTION_BASE}${path}`;
}

// -- Factories -----------------------------------------------------------

/**
 * Site-wide LocalBusiness identity. Uses the more specific
 * HomeAndConstructionBusiness type (subclass of LocalBusiness) for stronger
 * vertical relevance in Google rich results.
 *
 * Service-area model: no `address` published; `areaServed` lists counties
 * we cover. `hasCredential` exposes the FL contractor license.
 */
export function buildLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'HomeAndConstructionBusiness',
    '@id': `${PRODUCTION_BASE}/#business`,
    name: BUSINESS_INFO.legalName,
    alternateName: BUSINESS_INFO.brandName,
    url: PRODUCTION_BASE,
    logo: PRODUCTION_LOGO_URL,
    image: PRODUCTION_LOGO_URL,
    telephone: BUSINESS_INFO.phone,
    priceRange: '$$',
    // Service-area-business shape (no street address)
    areaServed: BUSINESS_INFO.serviceAreas.map((county) => ({
      '@type': 'AdministrativeArea',
      name: county,
      containedInPlace: {
        '@type': 'State',
        name: 'Florida',
      },
    })),
    // Same as above but as plain region too (Google sometimes prefers either form)
    serviceArea: {
      '@type': 'GeoShape',
      addressCountry: 'US',
      // No coords / radius until we have a verified dispatch address
    },
    openingHoursSpecification: BUSINESS_INFO.hours.map(({ days, opens, closes }) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: days,
      opens,
      closes,
    })),
    hasCredential: {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'license',
      name: 'Florida Certified General Contractor',
      identifier: BUSINESS_INFO.licenseNumber,
      recognizedBy: {
        '@type': 'Organization',
        name: 'Florida Department of Business & Professional Regulation',
      },
    },
    knowsAbout: [
      'Impact windows',
      'Hurricane impact doors',
      'Florida Building Code',
      'NOA (Notice of Acceptance) compliance',
      'HOA-compliant window replacement',
    ],
  };
}

/**
 * FAQPage schema. Pass an array of `{question, answer}` items already
 * displayed as visible content on the page. Returns `null` if no
 * usable items so callers can `[...maybe].filter(Boolean)`.
 */
export function buildFAQSchema(items) {
  const cleaned = (items || [])
    .map(({ question, answer }) => ({
      question: (question || '').trim(),
      answer: (answer || '').trim(),
    }))
    .filter((it) => it.question && it.answer);
  if (cleaned.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: cleaned.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };
}

/**
 * Article schema for case studies / long-form informational pages.
 * Caller MUST pass a real headline + description that match the visible
 * page H1/meta description.
 */
export function buildArticleSchema({
  url,
  headline,
  description,
  datePublished,
  dateModified,
  imageUrl,
  authorName,
}) {
  if (!headline || !url) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': toProductionUrl(url),
    },
    headline,
    description: description || '',
    image: imageUrl ? toProductionUrl(imageUrl) : PRODUCTION_LOGO_URL,
    datePublished: datePublished || undefined,
    dateModified: dateModified || datePublished || undefined,
    author: {
      '@type': 'Organization',
      name: authorName || BUSINESS_INFO.legalName,
    },
    publisher: {
      '@type': 'Organization',
      name: BUSINESS_INFO.legalName,
      logo: {
        '@type': 'ImageObject',
        url: PRODUCTION_LOGO_URL,
      },
    },
  };
}

/**
 * Generic WebPage schema for thin LP pages (Cost Miami, No Sales) where
 * we still want a canonical/title signal but Article would be a stretch.
 */
export function buildWebPageSchema({ url, name, description }) {
  if (!name || !url) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': toProductionUrl(url),
    url: toProductionUrl(url),
    name,
    description: description || '',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${PRODUCTION_BASE}/#website`,
      name: BUSINESS_INFO.brandName,
      url: PRODUCTION_BASE,
    },
    publisher: { '@id': `${PRODUCTION_BASE}/#business` },
  };
}

/**
 * BreadcrumbList — only call when the page actually renders breadcrumbs
 * to the user (per Google's guideline: schema must reflect visible UI).
 *
 * Items: [{label, link?}]  (last item may omit `link` — that's the
 * current page).
 */
export function buildBreadcrumbSchema(items) {
  const cleaned = (items || []).filter((it) => it && it.label);
  if (cleaned.length < 2) return null; // need at least Home > Current
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: cleaned.map((it, idx) => {
      const node = {
        '@type': 'ListItem',
        position: idx + 1,
        name: it.label,
      };
      if (it.link) {
        // Only include `item` for pages that actually have a destination —
        // the trailing current-page entry should not have one.
        node.item = toProductionUrl(it.link);
      }
      return node;
    }),
  };
}

/**
 * Service schema — declares the actual service offered. Useful on city
 * pages and the homepage. Single canonical service definition.
 */
export function buildServiceSchema({ areaName } = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Impact Window & Door Installation',
    provider: { '@id': `${PRODUCTION_BASE}/#business` },
    areaServed: areaName
      ? { '@type': 'AdministrativeArea', name: areaName }
      : BUSINESS_INFO.serviceAreas.map((c) => ({
          '@type': 'AdministrativeArea',
          name: c,
        })),
    description:
      'Impact window and door replacement in South Florida — Good/Better/Premium tiers, no in-home appointment required.',
  };
}
